import { useEffect, useRef } from 'react';
import { AgentModal } from '@/entities/agent';
import { ChatDropzone, ChatInput } from '@/features/chats';
import { getChatStores } from '@/features/chats/lib/chat-store-bindings';
import { ChatSettingsPersistenceMount } from '../ChatSettingsPersistenceMount/ChatSettingsPersistenceMount';
import { CHAT_COLUMN_MARKER } from '@/shared/lib/chat-layout';
import { ChatSessionLayoutProps } from './ChatSessionLayout.types';

/**
 * ChatSessionLayout - Simplified layout for chat content only.
 *
 * The side panel is now rendered in SessionsLayout to persist across route changes.
 * This component only handles the chat content area (thread + input).
 */
export function ChatSessionLayout({
  children,
  onSend,
  onStop,
  isNewChat,
  projectContextDeps,
  sendBlockedReason,
  busyLabel,
  footerEnd,
  inputTop,
  debugControls,
  dropzone = true,
}: ChatSessionLayoutProps) {
  const { isModalOpen, modalAgentId, closeAgentModal } = getChatStores().uiStore.useAgentModal();
  const chatContainerRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom on load, re-triggering on layout shifts (e.g., side panel sliding out)
  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container) return;

    let forceScroll = true;

    const scrollToBottom = () => {
      if (forceScroll) {
        container.scrollTop = container.scrollHeight;
        return;
      }

      const isNearBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight < 100;

      if (isNearBottom) {
        container.scrollTop = container.scrollHeight;
      }
    };

    // Detect user scroll and disable force scrolling
    const handleUserScroll = () => {
      forceScroll = false;
    };

    const resizeObserver = new ResizeObserver(() => {
      scrollToBottom();
    });

    resizeObserver.observe(container);
    container.addEventListener('wheel', handleUserScroll, { passive: true });
    container.addEventListener('touchmove', handleUserScroll, { passive: true });

    // Initial scroll
    scrollToBottom();

    // After 1000ms, switch to smart scrolling (respects user's scroll position)
    const smartScrollTimeout = setTimeout(() => {
      forceScroll = false;
    }, 1000);

    // Stop observing after layout has settled (3 seconds)
    const cleanupTimeout = setTimeout(() => {
      resizeObserver.disconnect();
    }, 3000);

    return () => {
      clearTimeout(smartScrollTimeout);
      clearTimeout(cleanupTimeout);
      container.removeEventListener('wheel', handleUserScroll);
      container.removeEventListener('touchmove', handleUserScroll);
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <>
      {/* Persists + restores templates-agent chat settings; runs for both new-chat and resume
          because both render this layout. Renders nothing; a no-op for non-templates agents. */}
      <ChatSettingsPersistenceMount />
      {dropzone && <ChatDropzone />}
      <div className="flex flex-col h-full pb-4">
        <div
          ref={chatContainerRef}
          {...CHAT_COLUMN_MARKER}
          className="flex-1 min-h-0 flex flex-col overflow-y-auto px-2 overflow-x-hidden"
        >
          {children}
        </div>
        <div className="flex items-end justify-center pt-3 sticky bottom-0 z-10 px-4">
          <ChatInput
            isNewChat={isNewChat}
            onSend={onSend}
            onStop={onStop}
            projectContextDeps={projectContextDeps}
            {...(sendBlockedReason ? { sendBlockedReason } : {})}
            {...(busyLabel ? { busyLabel } : {})}
            {...(footerEnd ? { footerEnd } : {})}
            {...(inputTop ? { inputTop } : {})}
            {...(debugControls ? { debugControls } : {})}
          />
        </div>
      </div>
      {modalAgentId && (
        <AgentModal open={isModalOpen} onClose={closeAgentModal} agentId={modalAgentId} />
      )}
    </>
  );
}
